import { beforeNavigate, refreshAll } from '$app/navigation';
import { fetch_json } from '#lib/util.js';
import { format_price } from '#lib/patch_util.js';
import { toast } from '#lib/toast.svelte.js';
import t from '#lib/i18n.svelte.js';

// What the meeting room and day pass calendars share for "select, then confirm": nothing is booked
// or cancelled until the member confirms in the booking sheet.

const PREVIEW_DELAY = 120;
const RECEIPT_DURATION = 6000;

/**
 * The selected items of one calendar, priced on the server after each change and confirmed in one
 * request. Create it during component initialisation: it clears itself when the owner (account or
 * resource) changes, and asks before a navigation drops it unconfirmed.
 */
export class BookingSelection {
  /** Replaced, never mutated, so a drag can keep the map it started from */
  items = $state.raw(new Map());
  /** The server's price for the selection (applied and rolled back there), or null */
  estimate = $state(null);
  error = $state(null);
  /** After a confirmed change: { title, lines, email } */
  receipt = $state(null);
  busy = $state(false);

  /** The items the estimate was priced for */
  #priced_items = $state.raw(null);
  #options;
  #preview_timer;
  #preview_sequence = 0;
  #receipt_timer;
  #owner;

  /**
   * @param {object} options
   * @param {string} options.api endpoint that applies the changes, or previews them with `preview: true`
   * @param {() => number} options.resource_id
   * @param {(items: Map) => object[]} options.changes the endpoint's change list for the items
   * @param {(response: object) => string | undefined} options.error_message the text for a refusal the calendar knows
   * @param {() => unknown} options.owner selections belong to one account and resource
   * @param {(key: string, value: unknown) => boolean} [options.still_selectable] after a refused
   *   confirmation the data is reloaded; items this rejects are dropped
   * @param {(response: object) => { keys: string[], label: string } | null} [options.failed_items] the
   *   items of the change a refusal names (response.change), when that change itself is the problem
   * @param {(response: object) => void} [options.on_refused]
   */
  constructor(options) {
    this.#options = options;
    $effect(() => {
      const owner = options.owner();
      if (this.#owner !== undefined && owner !== this.#owner) this.reset();
      this.#owner = owner;
    });

    // Leaving the calendar drops a selection that was never confirmed, so ask first. Other weeks
    // and months of the same calendar keep it. A page unload gets the browser's own dialog instead.
    beforeNavigate(({ cancel, from, to, willUnload }) => {
      if (willUnload || !this.items.size || this.busy) return;
      if (to?.url.pathname === from?.url.pathname) return;
      if (!confirm(t('booking_discard_selection'))) cancel();
    });
    $effect(() => {
      if (!this.items.size) return;
      const warn = event => {
        event.preventDefault();
        event.returnValue = '';
      };
      window.addEventListener('beforeunload', warn);
      return () => window.removeEventListener('beforeunload', warn);
    });
  }

  /** The shown price belongs to an earlier selection while the current one is being priced */
  get estimate_stale() {
    return this.estimate !== null && this.#priced_items !== this.items;
  }

  /** After a gesture: the last receipt and message go, the new selection is priced */
  changed() {
    this.receipt = null;
    this.error = null;
    this.#preview();
  }

  clear() {
    this.items = new Map();
    this.changed();
  }

  reset() {
    clearTimeout(this.#preview_timer);
    this.#preview_sequence++;
    this.items = new Map();
    this.estimate = null;
    this.error = null;
    this.receipt = null;
  }

  dismiss_receipt() {
    clearTimeout(this.#receipt_timer);
    this.receipt = null;
  }

  #message(response) {
    return this.#options.error_message(response)
      ?? (response.error === 'NOT_AUTHORIZED' ? t('alert_booking_not_authorized') : t('alert_generic_error'));
  }

  // A response or reload only applies to the selection it was started for: the member may have
  // corrected it, started another gesture or switched rooms in the meantime
  #snapshot() {
    return { sequence: this.#preview_sequence, items: this.items, owner: this.#options.owner() };
  }

  #is_current({ sequence, items, owner }) {
    return sequence === this.#preview_sequence && items === this.items && owner === this.#options.owner();
  }

  // keep_error: a refusal's message stays until the member changes the selection
  #preview({ keep_error = false } = {}) {
    clearTimeout(this.#preview_timer);
    this.#preview_sequence++;
    if (!this.items.size) {
      this.estimate = null;
      return;
    }
    const snapshot = this.#snapshot();
    const resource_id = this.#options.resource_id();
    const changes = this.#options.changes(snapshot.items);
    this.#preview_timer = setTimeout(async () => {
      try {
        const response = await fetch_json('POST', this.#options.api, { resource_id, changes, preview: true });
        if (!this.#is_current(snapshot)) return;
        if (response.success) {
          this.estimate = response.estimate;
          this.#priced_items = snapshot.items;
          if (!keep_error) this.error = null;
        } else {
          this.estimate = null;
          await this.#refused(response);
        }
      } catch (err) {
        if (this.#is_current(snapshot)) this.estimate = null;
      }
    }, PREVIEW_DELAY);
  }

  // A refused preview or confirmation. Its message stays until the selection changes. A change the
  // server names as no longer possible is dropped and named, also when its week is not shown;
  // after a refused confirmation (or a dropped change) the data is reloaded, and what can no
  // longer be selected goes too.
  async #refused(response, { confirming = false } = {}) {
    const failed = response.change ? this.#options.failed_items?.(response) : null;
    const message = failed ? `${failed.label}: ${this.#message(response)}` : this.#message(response);
    this.error = message;
    if (confirming) this.#options.on_refused?.(response);
    if (!failed && !confirming) return;

    const snapshot = this.#snapshot();
    await refreshAll();
    if (!this.#is_current(snapshot)) return;
    const { still_selectable } = this.#options;
    const next = new Map([...this.items].filter(([key, value]) =>
      !failed?.keys.includes(key) && (!still_selectable || still_selectable(key, value))
    ));
    if (next.size === this.items.size) return;
    this.items = next;
    // The sheet closes with an empty selection, and the message must outlast it
    if (!next.size) toast.error(message, { auto_dismiss: true, show_close_icon: true, id: 'booking-refused' });
    // The estimate must describe what is left
    this.#preview({ keep_error: true });
  }

  /** Confirms the selection and shows `receipt` afterwards */
  async commit(receipt) {
    if (this.busy || !this.items.size) return;
    this.busy = true;
    this.error = null;
    clearTimeout(this.#preview_timer);
    this.#preview_sequence++;
    try {
      const response = await fetch_json('POST', this.#options.api, { resource_id: this.#options.resource_id(), changes: this.#options.changes(this.items) });
      if (response.success) {
        this.items = new Map();
        this.estimate = null;
        // Only when an e-mail follows: cancelling a booking whose confirmation has not gone out
        // yet, for one, sends none
        this.receipt = { ...receipt, email: response.notify ? receipt.email : null };
        clearTimeout(this.#receipt_timer);
        this.#receipt_timer = setTimeout(() => (this.receipt = null), RECEIPT_DURATION);
        await refreshAll();
        return;
      }
      if (response.error === 'NOT_AUTHORIZED') {
        // The session has ended: the selection cannot be confirmed any more (on_refused logs in again)
        this.reset();
        this.#options.on_refused?.(response);
        return;
      }
      await this.#refused(response, { confirming: true });
    } catch (err) {
      console.error('Unexpected error:', err);
      this.error = t('alert_generic_error');
    } finally {
      this.busy = false;
    }
  }
}

export const signed_price = cents => (cents < 0 ? `−${format_price(-cents)}` : format_price(cents));

export const storno_count = count => t(count === 1 ? 'booking_storno' : 'booking_stornos', { count });

/** The sheet's action labels for n_add bookings and n_remove cancellations; unit(count) names the thing */
export function sheet_labels(n_add, n_remove, unit) {
  const both = n_add > 0 && n_remove > 0;
  return {
    cta: both
      ? t('booking_book_and_cancel', { book: n_add, cancel: n_remove })
      : n_add ? t('booking_book_count', { count: n_add, unit: unit(n_add) }) : t('booking_cancel_count', { count: n_remove, unit: unit(n_remove) }),
    peek_cta: t(both ? 'booking_confirm' : n_add ? 'booking_book' : 'booking_cancel'),
    working: t(both ? 'booking_working_both' : n_add ? 'booking_working_book' : 'booking_working_cancel'),
    mini: t(both ? 'booking_mini_changes' : n_add ? 'booking_mini_selected' : 'booking_mini_cancel'),
    receipt_title: t(both ? 'booking_receipt_changed' : n_add ? 'booking_receipt_booked' : 'booking_receipt_cancelled'),
  };
}

export function billing_line(current_user) {
  // Only the confirmed response knows whether an e-mail follows; the receipt shows that promise.
  return current_user?.role === 'admin' ? t('booking_internal') : t('booking_invoice_to', { name: current_user?.name });
}

/** One line per day for the receipt, cancellations marked */
export function receipt_lines(groups) {
  return groups.map(group => `${group.label}: ${group.rows.map(row => (row.kind === 'remove' ? `${row.time} ${t('booking_cancelled_suffix')}` : row.time)).join(', ')}`);
}
